import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { ApiError } from '@/api/client'
import { DataTable, type Column } from '@/components/DataTable'
import { RouteMap } from '@/components/RouteMap'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Checkbox } from '@/components/ui/Checkbox'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { useAuth } from '@/features/auth/store'
import { listCustomers } from '@/features/customers/api'
import {
  formatDistance,
  formatDuration,
  useCreateRoute,
  useRouteReps,
  useRoutes,
  type RouteRead,
  type RouteRep,
  type RouteStop,
} from '@/features/field_routes/api'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

function isoDay(offset = 0): string {
  const day = new Date()
  day.setDate(day.getDate() + offset)
  return `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`
}

/**
 * A stop with no promised time is stored at midnight UTC on its day so the day's lists
 * still find it (see `route_service.untimed`). That instant is "any time", not 05:30.
 */
function promisedTime(stop: RouteStop): string {
  if (!stop.planned_at || stop.planned_at.includes('T00:00:00')) return 'Any time'
  return new Date(stop.planned_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function PlanRouteDialog({ onClose, reps }: { onClose: () => void; reps: RouteRep[] }) {
  const create = useCreateRoute()
  const [repId, setRepId] = useState('')
  const [routeDate, setRouteDate] = useState(isoDay(1))
  const [search, setSearch] = useState('')
  const [onlyTheirs, setOnlyTheirs] = useState(true)
  const [picked, setPicked] = useState<{ id: string; name: string }[]>([])
  const [times, setTimes] = useState<Record<string, string>>({})
  const [optimize, setOptimize] = useState(true)

  const customers = useQuery({
    queryKey: ['customers', 'route-picker', search, onlyTheirs ? repId : ''],
    queryFn: () =>
      listCustomers({
        q: search || undefined,
        status: 'active',
        assigned_rep_id: onlyTheirs && repId ? repId : undefined,
      }),
    retry: false,
  })

  const pickedIds = new Set(picked.map((p) => p.id))

  function toggle(customer: { id: string; name: string }) {
    setPicked((current) =>
      pickedIds.has(customer.id)
        ? current.filter((p) => p.id !== customer.id)
        : [...current, customer],
    )
  }

  function submit() {
    const visitTimes = Object.fromEntries(
      Object.entries(times).filter(([id, time]) => time && pickedIds.has(id)),
    )
    create.mutate(
      {
        route_date: routeDate,
        customer_ids: picked.map((p) => p.id),
        optimize,
        user_id: repId || undefined,
        visit_times: visitTimes,
      },
      {
        onSuccess: (route) => {
          toast.success(`${route.route_number} planned with ${route.stops.length} stops.`)
          onClose()
        },
        onError: (error: unknown) => {
          toast.error(
            error instanceof ApiError
              ? error.message
              : 'Could not reach the server. Nothing was changed.',
          )
        },
      },
    )
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Plan a route"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={create.isPending}
            disabled={picked.length === 0 || !routeDate}
            onClick={submit}
          >
            Plan {picked.length || ''} stop{picked.length === 1 ? '' : 's'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            Rep
            <select
              className={`${SELECT_CLASS} mt-1 w-full`}
              value={repId}
              onChange={(event) => setRepId(event.target.value)}
            >
              <option value="">Me</option>
              {reps.map((rep) => (
                <option key={rep.id} value={rep.id}>
                  {rep.full_name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            Date
            <Input
              type="date"
              value={routeDate}
              min={isoDay()}
              onChange={(event) => setRouteDate(event.target.value)}
              className="mt-1"
            />
          </label>
        </div>

        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <Input
              placeholder="Search shops"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="min-w-0 flex-1"
            />
            {repId && (
              <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <Checkbox checked={onlyTheirs} onChange={(e) => setOnlyTheirs(e.target.checked)} />
                Only this rep's shops
              </label>
            )}
          </div>
          <ul className="max-h-48 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200 dark:divide-slate-800 dark:border-slate-700">
            {(customers.data?.data ?? []).map((customer) => (
              <li key={customer.id}>
                <label className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm">
                  <Checkbox
                    checked={pickedIds.has(customer.id)}
                    onChange={() => toggle({ id: customer.id, name: customer.name })}
                  />
                  <span className="flex-1">{customer.name}</span>
                  <span className="text-xs text-slate-500">{customer.city ?? ''}</span>
                </label>
              </li>
            ))}
            {customers.data?.data.length === 0 && (
              <li className="px-3 py-2 text-sm text-slate-500">No shops match.</li>
            )}
          </ul>
        </div>

        {picked.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Stops — set a time only where one was promised
            </p>
            <ul className="space-y-1">
              {picked.map((stop) => (
                <li key={stop.id} className="flex items-center gap-2 text-sm">
                  <span className="flex-1 truncate">{stop.name}</span>
                  <Input
                    type="time"
                    aria-label={`Time for ${stop.name}`}
                    value={times[stop.id] ?? ''}
                    onChange={(event) => setTimes((t) => ({ ...t, [stop.id]: event.target.value }))}
                    className="w-32"
                  />
                  <Button variant="ghost" size="sm" onClick={() => toggle(stop)}>
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
          <Checkbox checked={optimize} onChange={(e) => setOptimize(e.target.checked)} />
          Optimise the order (shortest drive, promised times, gold customers first)
        </label>
      </div>
    </Modal>
  )
}

function RouteDetail({ route, repName }: { route: RouteRead; repName: string }) {
  return (
    <Card>
      <CardHeader
        title={`${route.route_number} · ${repName} · ${route.route_date}`}
        description={`${route.stops.length} stops, ${formatDistance(route.total_distance_m)}, ${formatDuration(route.total_duration_s)} driving${
          route.distance_provider === 'haversine' ? ' (straight-line estimate)' : ''
        }`}
      />
      <CardBody className="grid gap-4 lg:grid-cols-2">
        <RouteMap stops={route.stops} />
        <ol className="space-y-2">
          {route.stops.map((stop) => (
            <li
              key={stop.visit_id}
              className="flex items-start gap-3 rounded-md border border-slate-200 p-2 text-sm dark:border-slate-700"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-600 text-xs font-semibold text-white">
                {stop.stop_sequence + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-medium">{stop.customer_name}</p>
                <p className="truncate text-xs text-slate-500">
                  {stop.customer_address ?? 'No address'}
                </p>
              </div>
              <div className="text-right text-xs text-slate-500">
                <p>{promisedTime(stop)}</p>
                <p>
                  {stop.leg_distance_m == null
                    ? 'No location'
                    : `${formatDistance(stop.leg_distance_m)} · ${formatDuration(stop.leg_duration_s)}`}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </CardBody>
    </Card>
  )
}

/**
 * Where a manager plans a rep's day. The rep drives it from the mobile screen; this is the
 * desk side: pick the rep, the shops and any promised times, and the optimiser orders it.
 */
export function FieldRoutes() {
  const can = useAuth((s) => s.can)
  const me = useAuth((s) => s.user)
  const [dateFrom, setDateFrom] = useState(isoDay())
  const [planning, setPlanning] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)

  const canPlan = can('field_operations', 'create')
  const reps = useRouteReps()
  const routes = useRoutes({ date_from: dateFrom || undefined })

  const names = new Map((reps.data ?? []).map((rep) => [rep.id, rep.full_name]))
  const repName = (id: string) => (id === me?.id ? 'Me' : (names.get(id) ?? 'Rep'))
  const rows = routes.data?.data ?? []
  const current = rows.find((route) => route.id === selected) ?? null

  const columns: Column<RouteRead>[] = [
    { key: 'route_number', header: 'Route', className: 'font-medium', cell: (r) => r.route_number },
    { key: 'route_date', header: 'Date', cell: (r) => r.route_date },
    { key: 'rep', header: 'Rep', cell: (r) => repName(r.user_id) },
    { key: 'stops', header: 'Stops', className: 'tabular-nums', cell: (r) => r.stops.length },
    { key: 'distance', header: 'Distance', cell: (r) => formatDistance(r.total_distance_m) },
    { key: 'status', header: 'Status', cell: (r) => r.status.replace('_', ' ') },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      cell: (r) => (
        <Button variant="ghost" size="sm" onClick={() => setSelected(r.id)}>
          View
        </Button>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Field routes</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Plan a rep's day of shop visits; the order is worked out for the shortest drive.
          </p>
        </div>
        {canPlan && <Button onClick={() => setPlanning(true)}>Plan a route</Button>}
      </header>

      {routes.error instanceof ApiError && <Alert tone="error">{routes.error.message}</Alert>}

      <Card>
        <DataTable
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          caption="Field routes"
          isLoading={routes.isLoading}
          error={routes.error}
          emptyMessage="No routes planned from this date."
          toolbar={
            <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              From
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </label>
          }
        />
      </Card>

      {current && <RouteDetail route={current} repName={repName(current.user_id)} />}

      {planning && (
        <PlanRouteDialog
          onClose={() => setPlanning(false)}
          reps={(reps.data ?? []).filter((rep) => rep.id !== me?.id)}
        />
      )}
    </div>
  )
}
